import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Stack,
  Zoom,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  AccessTime as TimeIcon,
  CalendarMonth as DateIcon,
  Person as PersonIcon,
  EventSeat as SeatIcon,
  ArrowForward as ArrowIcon,
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  LocationOn as LocationIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import { timelineItemClasses } from '@mui/lab/TimelineItem';

import { useUserProfile } from '../contexts/UserProfileContext';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const RideDetailsModal = ({ isOpen, onClose, ride, onJoin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [selectedSeats, setSelectedSeats] = useState(1); // Default to 1 seat if not selected
  const { openProfile } = useUserProfile();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Check if saved when ride changes
  React.useEffect(() => {
    if (isOpen && ride && user) {
      checkIfSaved();
    }
  }, [isOpen, ride, user]);

  const checkIfSaved = async () => {
    try {
      const response = await apiService.get('/saved-rides');
      if (response.success) {
        const saved = response.data.some(r => r.id === ride.id);
        setIsSaved(saved);
      }
    } catch (error) {
      console.error('Check if saved error:', error);
    }
  };

  const handleToggleSave = async (e) => {
    e.stopPropagation();
    if (!user) {
      alert(t('rideDetails.saveLoginRequiredMessage', 'Please sign in to save rides for later.'));
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.post(`/saved-rides/${ride.id}`);
      if (response.success) {
        setIsSaved(response.saved);
      }
    } catch (error) {
      console.error('Toggle save error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Normalize ride data to handle different structures (backend object vs simple props)
  const rideData = useMemo(() => {
    if (!ride) return null;

    // Check if it's the complex backend object
    const isComplex = ride.originCity && ride.destinationCity;

    const formattedDate = ride.departureDateTime ? new Date(ride.departureDateTime) : null;

    return {
      ...ride,
      id: ride.id,
      date: isComplex && formattedDate ? formattedDate.toLocaleDateString() : (ride.date || ''),
      time: isComplex && formattedDate ? formattedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (ride.time || ''),
      origin: isComplex ? `${ride.originCity.name}, ${ride.originCity.province}` : (ride.origin || ''),
      destination: isComplex ? `${ride.destinationCity.name}, ${ride.destinationCity.province}` : (ride.destination || ''),
      driverName: isComplex && ride.driver ? `${ride.driver.preferredName || ride.driver.firstName || ''} ${ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}` : (ride.driverName || 'Unknown Driver'),
      driverId: isComplex && ride.driver ? ride.driver.id : (ride.driverId || null),
      driverProfilePicture: isComplex && ride.driver ? ride.driver.profilePicture : (ride.driverProfilePicture || null),
      price: ride.pricePerPerson !== undefined ? ride.pricePerPerson : (ride.price || 0),
      availableSeats: ride.availableSeats !== undefined ? ride.availableSeats : 4
    };
  }, [ride]);

  if (!rideData) return null;
  // Use rideData instead of ride for the rest of the component (except key props if needed)
  // We will shadow 'ride' with 'rideData' to avoid changing all references
  const displayRide = rideData;

  const handleJoin = () => {
    // Check if user is a member (authenticated)
    const isAuthenticated = !!localStorage.getItem('rideclub_token');

    if (!isAuthenticated) {
      // Redirect to signup and pass the destination in state for the Signup component to use
      navigate({
        pathname: '/register',
        search: `?redirect=${encodeURIComponent('/find-rides?rideId=' + displayRide.id)}`,
      });
      return;
    }

    // If authenticated, join the ride (navigate to Ride Details Page to book)
    onJoin(displayRide.id, selectedSeats);
  };

  const availableSeats = displayRide.availableSeats;
  const seatOptions = Array.from({ length: availableSeats }, (_, i) => i + 1);

  // Modern Glass/Gradient Styles
  const glassStyle = {
    background: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.8)
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
  };

  const headerGradient = `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`;

  return (
    <>
      <Dialog
        open={!!isOpen}
        onClose={onClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            ...glassStyle,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        {/* Header Section */}
        <Box sx={{
          position: 'relative',
          p: 3,
          background: headerGradient,
          color: '#fff',
          overflow: 'hidden'
        }}>
          {/* Decorative Circle BG */}
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            pointerEvents: 'none'
          }} />

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" position="relative" zIndex={1}>
            <Box>
              <Chip
                label={`#${displayRide.id?.toString().slice(-5)}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(0,0,0,0.2)',
                  color: '#fff',
                  fontWeight: 'bold',
                  mb: 1,
                  backdropFilter: 'blur(4px)'
                }}
              />
              <Typography variant="h4" fontWeight="800" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                {t('rideDetails.title', 'Ride Details')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Driver Section */}
          <Box
            sx={{
              p: 3,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: displayRide.driverId ? 'pointer' : 'default',
              transition: 'opacity 0.2s',
              '&:hover': displayRide.driverId ? { opacity: 0.8 } : {},
              position: 'relative',
              zIndex: 1
            }}
            onMouseDown={(e) => {
              // Prevent any mouse event from propagating
              e.stopPropagation();
            }}
            onClick={(e) => {
              // CRITICAL: Stop ALL propagation to prevent closing parent modals
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();

              if (displayRide.driverId) {
                console.log('[RideDetailsModal] Driver section clicked, opening profile for ID:', displayRide.driverId);
                // Use setTimeout to ensure this happens AFTER the current event cycle
                setTimeout(() => {
                  openProfile(String(displayRide.driverId));
                }, 0);
              }
            }}
          >
            <Avatar
              src={displayRide.driverProfilePicture}
              alt={displayRide.driverName}
              sx={{
                width: 56,
                height: 56,
                bgcolor: theme.palette.primary.light,
                boxShadow: theme.shadows[2],
                pointerEvents: 'none' // Prevent avatar from being the click target
              }}
            >
              {displayRide.driverName ? displayRide.driverName.charAt(0).toUpperCase() : <PersonIcon />}
            </Avatar>
            <Box sx={{ pointerEvents: 'none' }}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="700">
                {t('rideDetails.driver', 'Driver')}
              </Typography>
              <Typography variant="h6" fontWeight="700">
                {displayRide.driverName}
              </Typography>
              {/* Optional: Add rating stars here */}
            </Box>
          </Box>

          <Divider sx={{ my: 1, mx: 3 }} />

          {/* Route Timeline */}
          <Box sx={{ px: 3 }}>
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
                p: 0,
                my: 1
              }}
            >
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot variant="outlined" color="primary" sx={{ border: '2px solid' }} />
                  <TimelineConnector sx={{ bgcolor: alpha(theme.palette.primary.main, 0.2), borderRadius: 1 }} />
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="700" textTransform="uppercase">
                        {t('rideDetails.origin', 'Origin')}
                      </Typography>
                      <Typography variant="body1" fontWeight="600">
                        {displayRide.origin}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={handleToggleSave}
                      disabled={saving}
                      sx={{
                        color: isSaved ? '#10b981' : theme.palette.text.secondary,
                        bgcolor: alpha(isSaved ? '#10b981' : theme.palette.action.active, 0.05),
                        '&:hover': {
                          bgcolor: alpha(isSaved ? '#10b981' : theme.palette.action.active, 0.1),
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease',
                        mt: -0.5
                      }}
                    >
                      {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                    </IconButton>
                  </Box>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot color="primary" sx={{ boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <LocationIcon fontSize="small" />
                  </TimelineDot>
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="700" textTransform="uppercase">
                    {t('rideDetails.destination', 'Destination')}
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {displayRide.destination}
                  </Typography>

                  <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 3, border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}` }}>
                    <Stack direction="row" spacing={4} alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">{t('rideDetails.dateLabel', 'DATE')}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DateIcon color="primary" />
                          <Typography variant="h6" fontWeight="800" color="primary.main">
                            {displayRide.date}
                          </Typography>
                        </Stack>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">{t('rideDetails.timeLabel', 'TIME')}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TimeIcon color="primary" />
                          <Typography variant="h6" fontWeight="800" color="primary.main">
                            {displayRide.time}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
          </Box>

          {/* Seat Selection */}
          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <SeatIcon color="action" />
                <Typography variant="subtitle1" fontWeight="700" sx={{ fontSize: '1.5rem' }}>
                  {t('rideDetails.selectSeats', 'Select Seats')}
                </Typography>
              </Box>
              <Chip
                label={t('rideDetails.availableSeatsCount', { count: availableSeats, defaultValue: `${availableSeats} available` })}
                size="medium"
                color={availableSeats < 2 ? 'error' : 'success'}
                variant="outlined"
                sx={{ fontSize: '1.125rem', fontWeight: 700, px: 1 }}
              />
            </Stack>

            <Stack direction="row" spacing={1.5} justifyContent="center">
              {seatOptions.map((num) => (
                <Box
                  key={num}
                  onClick={() => setSelectedSeats(num)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 18,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    bgcolor: selectedSeats === num ? theme.palette.primary.main : theme.palette.background.paper,
                    color: selectedSeats === num ? '#fff' : theme.palette.text.primary,
                    boxShadow: selectedSeats === num
                      ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
                      : theme.shadows[1],
                    border: `2px solid ${selectedSeats === num ? theme.palette.primary.main : theme.palette.divider}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4]
                    }
                  }}
                >
                  {num}
                </Box>
              ))}
            </Stack>
          </Box>
        </DialogContent>

        {/* Footer / Action */}
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2, bgcolor: theme.palette.background.paper }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="body2" color="text.secondary" fontWeight="600">
              {t('rideDetails.totalPriceLabel', 'Total Price')}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <MoneyIcon fontSize="large" color="primary" />
              <Typography variant="h3" fontWeight="800" color="text.primary">
                {(displayRide.price * (selectedSeats || 0)).toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={handleJoin}
            disabled={!selectedSeats}
            fullWidth
            variant="contained"
            size="large"
            endIcon={<ArrowIcon />}
            sx={{
              py: 2,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.5)}`,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('joinRide', 'Join Ride')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default RideDetailsModal;